/**
 * walleu-video-carousel
 * - Only the centered slide plays, always restarting from the beginning.
 * - Desktop coverflow: centered slide larger, sides smaller and clickable.
 * - Optional infinite loop: the set is cloned on both sides and the scroll position is
 *   shifted by exactly one set once the scroll settles, so it never visibly ends.
 *   Clones carry no video data (preload="none"), so only the real videos ever load.
 * - Section pauses when scrolled out of view (IntersectionObserver, no polling).
 * - Native CSS scroll-snap (no Swiper). Scoped per instance.
 */
(function () {
  class WalleuVideoCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-track]');
      this.prevBtn = root.querySelector('[data-prev]');
      this.nextBtn = root.querySelector('[data-next]');
      this.slides = Array.from(this.track.querySelectorAll('[data-slide]'));
      this.realCount = this.slides.length;

      // Stories variant: a row of thumbnails that open large in a modal on click
      // (no coverflow, no loop, no inline autoplay).
      if (root.classList.contains('walleu-video-carousel--stories')) {
        this.initStories();
        return;
      }

      this.loop = root.dataset.loop === 'true' && this.realCount >= 3;
      this.centeredIndex = -1;
      this.inView = false;
      this._raf = null;
      this._endTimer = null;

      if (this.loop) this.buildClones();
      this.setupSound();
      this.setupClickToCenter();
      this.setupNav();
      this.setupScroll();
      this.setupViewportPause();

      requestAnimationFrame(() => {
        const start = this.loop ? this.realCount : 0; // first real slide
        this.centerSlide(start, false);
        this.setCentered(start);
        this.updateArrows();
      });
      window.addEventListener('resize', () => this.centerSlide(this.centeredIndex, false), { passive: true });
    }

    /* Stories: click a thumbnail to open it large in the modal player. */
    initStories() {
      this.modal = this.root.querySelector('[data-modal]');
      this.modalBody = this.root.querySelector('[data-modal-body]');

      this.track.addEventListener('click', (e) => {
        const slide = e.target.closest('[data-slide]');
        if (slide) this.openModal(slide);
      });

      this.setupNav();
      this.updateArrows();
      this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });

      if (this.modal) {
        const closeBtn = this.modal.querySelector('[data-modal-close]');
        if (closeBtn) closeBtn.addEventListener('click', () => this.modal.close());
        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) this.modal.close(); // click on backdrop
        });
        this.modal.addEventListener('close', () => {
          if (this.modalBody) this.modalBody.innerHTML = ''; // stop the video
        });
      }
    }

    openModal(slide) {
      const source = slide.querySelector('video');
      if (!this.modal || !this.modalBody || !source) return;
      const clone = source.cloneNode(true); // carries its <source> children
      clone.muted = false;
      clone.controls = true;
      clone.loop = false;
      clone.setAttribute('playsinline', '');
      clone.preload = 'auto';
      clone.removeAttribute('poster');
      this.modalBody.innerHTML = '';
      this.modalBody.appendChild(clone);
      if (typeof this.modal.showModal === 'function') this.modal.showModal();
      else this.modal.setAttribute('open', 'open');
      clone.play().catch(() => {});
    }

    buildClones() {
      const originals = this.slides;
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      originals.forEach((s) => {
        before.appendChild(this.makeClone(s));
        after.appendChild(this.makeClone(s));
      });
      this.track.insertBefore(before, originals[0]);
      this.track.appendChild(after);
      this.slides = Array.from(this.track.querySelectorAll('[data-slide]'));
    }

    makeClone(slide) {
      const clone = slide.cloneNode(true);
      clone.dataset.clone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('video').forEach((v) => {
        v.preload = 'none'; // clones show only their poster; no video data is fetched
        v.removeAttribute('autoplay');
      });
      clone.querySelectorAll('button').forEach((b) => (b.tabIndex = -1));
      return clone;
    }

    getCenteredIndex() {
      const t = this.track.getBoundingClientRect();
      const center = t.left + t.width / 2;
      let best = 0;
      let bestDist = Infinity;
      this.slides.forEach((slide, i) => {
        const r = slide.getBoundingClientRect();
        const dist = Math.abs(r.left + r.width / 2 - center);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    /* Scroll so slide i sits in the centre. */
    centerSlide(i, smooth = true) {
      const slide = this.slides[i];
      if (!slide) return;
      const s = slide.getBoundingClientRect();
      const t = this.track.getBoundingClientRect();
      const delta = s.left + s.width / 2 - (t.left + t.width / 2);
      this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: smooth ? 'smooth' : 'auto' });
    }

    /* Mark the centred slide, play its video (originals only), pause/reset the rest. */
    setCentered(i) {
      if (i === this.centeredIndex) return;
      this.slides[this.centeredIndex]?.classList.remove('is-active');
      this.centeredIndex = i;
      const active = this.slides[i];
      active?.classList.add('is-active');
      this.slides.forEach((slide, idx) => {
        if (idx === i) return;
        const v = slide.querySelector('video');
        if (v && !v.paused) v.pause();
        if (v) v.currentTime = 0;
      });
      const v = active?.querySelector('video');
      if (v && this.inView && !active.dataset.clone) {
        v.currentTime = 0;
        v.play().catch(() => {});
      }
    }

    setupScroll() {
      this.track.addEventListener(
        'scroll',
        () => {
          if (!this._raf) {
            this._raf = requestAnimationFrame(() => {
              this._raf = null;
              this.setCentered(this.getCenteredIndex());
              this.updateArrows();
            });
          }
          // Wrap only once the scroll has settled, so we never cut off momentum.
          clearTimeout(this._endTimer);
          this._endTimer = setTimeout(() => this.wrapIfNeeded(), 120);
        },
        { passive: true }
      );
    }

    /* Seamless infinite loop: jump by exactly one set when the centre lands on a clone. */
    wrapIfNeeded() {
      if (!this.loop) return;
      let i = this.getCenteredIndex();
      const setWidth = this.slides[this.realCount].getBoundingClientRect().left - this.slides[0].getBoundingClientRect().left;
      if (i < this.realCount) {
        this.track.scrollLeft += setWidth;
        i += this.realCount;
      } else if (i >= this.realCount * 2) {
        this.track.scrollLeft -= setWidth;
        i -= this.realCount;
      } else {
        return;
      }
      this.setCentered(i); // now an original; identical content, so the jump is invisible
    }

    setupClickToCenter() {
      this.track.addEventListener('click', (e) => {
        if (e.target.closest('[data-sound]')) return;
        const slide = e.target.closest('[data-slide]');
        if (!slide) return;
        const i = this.slides.indexOf(slide);
        if (i === -1 || i === this.centeredIndex) return;
        this.centerSlide(i);
      });
    }

    setupSound() {
      this.track.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-sound]');
        if (!btn) return;
        const video = btn.closest('[data-slide]').querySelector('video');
        if (!video) return;
        video.muted = !video.muted;
        btn.setAttribute('aria-pressed', String(!video.muted));
        btn.setAttribute('aria-label', video.muted ? btn.dataset.labelUnmute : btn.dataset.labelMute);
      });
    }

    setupNav() {
      if (!this.prevBtn || !this.nextBtn) return;
      const step = () => {
        const first = this.slides[0];
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
        return first ? first.getBoundingClientRect().width + gap : this.track.clientWidth;
      };
      this.prevBtn.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.nextBtn.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
    }

    updateArrows() {
      if (!this.prevBtn || !this.nextBtn) return;
      if (this.loop) {
        this.prevBtn.hidden = false;
        this.nextBtn.hidden = false;
        return;
      }
      const max = this.track.scrollWidth - this.track.clientWidth - 1;
      this.prevBtn.hidden = this.track.scrollLeft <= 0;
      this.nextBtn.hidden = this.track.scrollLeft >= max;
    }

    setupViewportPause() {
      if (!('IntersectionObserver' in window)) {
        this.inView = true;
        return;
      }
      new IntersectionObserver(
        (entries) => {
          this.inView = entries[0].isIntersecting;
          const active = this.slides[this.centeredIndex]?.querySelector('video');
          if (!active) return;
          if (this.inView && !this.slides[this.centeredIndex].dataset.clone) {
            active.currentTime = 0;
            active.play().catch(() => {});
          } else {
            this.slides.forEach((s) => s.querySelector('video')?.pause());
          }
        },
        { threshold: 0.25 }
      ).observe(this.root);
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-walleu-video-carousel]').forEach((root) => {
      if (root.dataset.inited) return;
      root.dataset.inited = 'true';
      new WalleuVideoCarousel(root);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', () => init());

  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
